import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { Text, TextInput } from 'react-native-paper';

import { CallIcon, LocationIcon, MailIcon, WebIcon } from '@/assets';
import { getClientErrorType } from '@/features/api/hooks/errorMiddleware';
import { useUpdateOrganizationPending } from '@/features/api/hooks/grapevine-organization/useUpdateOrganization';
import { AvatarWithMenu } from '@/features/media/components/menu/AvatarWithMenu';
import { EMPTY_AVATAR_PICKER_RESULT } from '@/features/media/types/picker';
import { DetailImageList } from '@/features/profile-setting/components/organization-edit/DetailImageList';
import { EditPreviewModal } from '@/features/profile-setting/components/organization-edit/EditPreviewModal';
import { FactListSection } from '@/features/profile-setting/components/organization-edit/FactListSection';
import { FormActions } from '@/features/profile-setting/components/organization-edit/FormActions';
import { DESCRIPTION_MAX_LENGTH } from '@/features/profile-setting/components/organization-edit/MultilineEditorModal';
import { MultilineTeaserField } from '@/features/profile-setting/components/organization-edit/MultilineTeaserField';
import { makeOrganizationEditFormStyles } from '@/features/profile-setting/components/organization-edit/organizationEditFormStyles';
import type {
  OrganizationEditFormProps,
  OrganizationEditFormValues
} from '@/features/profile-setting/components/organization-edit/types';
import { organizationEditSchema } from '@/features/profile-setting/schemas/organizationEdit';
import { normalizeUrlForPayload } from '@/features/profile-setting/utils/organizationFormValidation';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { FormTextInput } from '@/shared/components/forms/FormTextInput';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

export const OrganizationEditForm: FC<OrganizationEditFormProps> = ({
  organization,
  onSave,
  onDelete
}) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-edit'
  });
  const { t: tApi } = useTranslation(LocalizationNameSpace.API);
  const styles = useMemo(() => makeOrganizationEditFormStyles(theme), [theme]);
  const isPending = useUpdateOrganizationPending();
  const iconColor = theme.colors.onSurfaceVariant;

  const [previewVisible, setPreviewVisible] = useState(false);

  const schema = useMemo(() => organizationEditSchema(t), [t]);

  const methods = useForm<OrganizationEditFormValues>({
    // Validate on submit; after a field has errored once, re-validate it on
    // change so the error clears as soon as the user fixes it.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
    // The screen only mounts this form once the organization is loaded, so
    // its data can seed the form directly — no reset effect needed.
    defaultValues: {
      name: organization.name ?? '',
      address: organization.address?.name ?? '',
      url: organization.url ?? '',
      phone: organization.phone ?? '',
      emailAddress: organization.emailAddress ?? '',
      // Cap to the editor's max so the seeded value matches what the
      // description text area displays (it truncates at DESCRIPTION_MAX_LENGTH)
      // and passes the schema's length check. Existing orgs whose stored
      // description exceeds the limit would otherwise fail validation on load.
      descriptionText: (organization.description ?? '').slice(
        0,
        DESCRIPTION_MAX_LENGTH
      ),
      detailImages: (organization.detailImages ?? []).map((item) => ({
        key: item.id,
        mediaItem: item
      })),
      facts: (organization.facts ?? []).map((fact, index) => ({
        key: `existing-${index}-${fact.name}`,
        name: fact.name,
        value: fact.value
      })),
      logoResult: EMPTY_AVATAR_PICKER_RESULT
    }
  });
  const { control, handleSubmit, setValue, setError, clearErrors } = methods;

  const submit = useCallback(
    async (data: OrganizationEditFormValues) => {
      // Drop any prior server-set error on `name` so a fresh submit isn't
      // blocked by stale UI (RHF doesn't clear setError'd errors on its own).
      clearErrors('name');
      try {
        await onSave({
          name: data.name.trim(),
          address: isEmptyOrNull((data.address ?? '').trim())
            ? undefined
            : (data.address ?? '').trim(),
          url: normalizeUrlForPayload(data.url ?? ''),
          phone: (data.phone ?? '').trim(),
          emailAddress: (data.emailAddress ?? '').trim(),
          description: data.descriptionText.trim(),
          logo: data.logoResult.logo,
          detailImages: data.detailImages.map((entry) =>
            entry.temporaryMediaItemId
              ? { temporaryMediaItemId: entry.temporaryMediaItemId }
              : { mediaItemId: entry.mediaItem?.id ?? '' }
          ),
          facts: data.facts.map(({ name, value }) => ({
            name: name.trim(),
            value: value.trim()
          }))
        });
      } catch (error) {
        // Name-in-geo-area conflict → field-level error; other errors are
        // already surfaced by the shared mutation toast. Cast to string: the
        // new type isn't in the installed generated API package yet.
        if (
          (getClientErrorType(error) as string | undefined) ===
          'ORGANIZATION_NAME_ALREADY_EXISTS_IN_GEO_AREA'
        ) {
          setError('name', {
            type: 'server',
            message: tApi('organization.errorNameAlreadyExistsInGeoArea')
          });
        }
      }
    },
    [clearErrors, onSave, setError, tApi]
  );

  return (
    <FormProvider {...methods}>
      <View style={styles.header}>
        <Text variant="headlineLarge" style={styles.centered}>
          {t('title')}
        </Text>
        <Text variant="bodyLarge" style={styles.centered}>
          {t('description')}
        </Text>
      </View>

      <View style={styles.avatar}>
        <AvatarWithMenu
          mediaItem={organization.logoImage}
          disabled={isPending}
          onChange={(next) =>
            setValue('logoResult', next, { shouldDirty: true })
          }
        />
      </View>

      <FormTextInput
        control={control}
        name="name"
        accessibilityLabel={t('fields.name')}
        accessibilityHint={t('accessibility.fieldHint')}
        mode="outlined"
        label={t('fields.name')}
        placeholder={t('placeholders.name')}
        disabled={isPending}
      />

      <Text variant="titleMedium" style={styles.sectionTitle}>
        {t('sections.contact')}
      </Text>

      <FormTextInput
        control={control}
        name="address"
        accessibilityLabel={t('fields.address')}
        accessibilityHint={t('accessibility.fieldHint')}
        mode="outlined"
        label={t('fields.address')}
        placeholder={t('placeholders.address')}
        disabled={isPending}
        left={
          <TextInput.Icon
            icon={({ size }) => (
              <LocationIcon fill={iconColor} width={size} height={size} />
            )}
          />
        }
      />

      <FormTextInput
        control={control}
        name="emailAddress"
        accessibilityLabel={t('fields.emailAddress')}
        accessibilityHint={t('accessibility.fieldHint')}
        mode="outlined"
        label={t('fields.emailAddress')}
        placeholder={t('placeholders.emailAddress')}
        keyboardType="email-address"
        autoCapitalize="none"
        disabled={isPending}
        left={
          <TextInput.Icon
            icon={({ size }) => (
              <MailIcon fill={iconColor} width={size} height={size} />
            )}
          />
        }
      />

      <FormTextInput
        control={control}
        name="phone"
        accessibilityLabel={t('fields.phone')}
        accessibilityHint={t('accessibility.fieldHint')}
        mode="outlined"
        label={t('fields.phone')}
        placeholder={t('placeholders.phone')}
        keyboardType="phone-pad"
        disabled={isPending}
        left={
          <TextInput.Icon
            icon={({ size }) => (
              <CallIcon fill={iconColor} width={size} height={size} />
            )}
          />
        }
      />

      <FormTextInput
        control={control}
        name="url"
        accessibilityLabel={t('fields.url')}
        accessibilityHint={t('accessibility.fieldHint')}
        mode="outlined"
        label={t('fields.url')}
        placeholder={t('placeholders.url')}
        keyboardType="url"
        autoCapitalize="none"
        disabled={isPending}
        left={
          <TextInput.Icon
            icon={({ size }) => (
              <WebIcon fill={iconColor} width={size} height={size} />
            )}
          />
        }
      />

      <DetailImageList />

      <MultilineTeaserField
        name="descriptionText"
        sectionTitle={t('sections.description')}
        placeholder={t('placeholders.description')}
        editorTitle={t('descriptionEditor.title')}
        editorPlaceholder={t('descriptionEditor.placeholder')}
        maxLength={DESCRIPTION_MAX_LENGTH}
        isPending={isPending}
        styles={styles}
      />

      <FactListSection />

      <FormActions
        isPending={isPending}
        onDelete={onDelete}
        onPreview={() => setPreviewVisible(true)}
        onSave={handleSubmit(submit)}
      />

      <EditPreviewModal
        variant="organization"
        visible={previewVisible}
        onRequestClose={() => setPreviewVisible(false)}
        organization={organization}
      />
    </FormProvider>
  );
};
